import React, { useEffect, useState } from 'react';
import { mfaService, MfaMethod } from '../../services/mfaService';

interface MfaChallengePanelProps {
  mfaToken: string;
  setupRequired: boolean;
  onComplete: (tokens: { access_token: string; expires_in: number; backup_codes?: string[] }) => void | Promise<void>;
  onError: (message: string) => void;
}

const MfaChallengePanel: React.FC<MfaChallengePanelProps> = ({
  mfaToken,
  setupRequired,
  onComplete,
  onError,
}) => {
  const [method, setMethod] = useState<MfaMethod>('totp');
  const [code, setCode] = useState('');
  const [secret, setSecret] = useState<string | null>(null);
  const [provisioningUri, setProvisioningUri] = useState<string | null>(null);
  const [loadingSetup, setLoadingSetup] = useState(setupRequired);
  const [submitting, setSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [pendingTokens, setPendingTokens] = useState<{ access_token: string; expires_in: number } | null>(null);

  useEffect(() => {
    if (!setupRequired) return;

    let cancelled = false;
    mfaService
      .startSetupLogin(mfaToken)
      .then((info) => {
        if (cancelled) return;
        setSecret(info.secret);
        setProvisioningUri(info.provisioning_uri);
      })
      .catch(() => {
        if (!cancelled) onError('Failed to start MFA setup. Try signing in again.');
      })
      .finally(() => {
        if (!cancelled) setLoadingSetup(false);
      });

    return () => {
      cancelled = true;
    };
  }, [setupRequired, mfaToken, onError]);

  const handleSendEmailCode = async () => {
    setSendingEmail(true);
    try {
      await mfaService.sendEmailCode(mfaToken);
      setEmailSent(true);
    } catch (err: any) {
      const detail = err.response?.data?.detail ?? err.response?.data?.message;
      onError(typeof detail === 'string' ? detail : 'Could not send email code.');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (method === 'totp' && code.length !== 6) {
      onError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    if (method === 'email' && code.length !== 6) {
      onError('Enter the 6-digit code from your email.');
      return;
    }
    if (method === 'backup' && code.replace(/[-\s]/g, '').length < 8) {
      onError('Enter a valid backup recovery code.');
      return;
    }

    setSubmitting(true);
    try {
      const tokens = await mfaService.completeLogin(mfaToken, code, method);
      if (tokens.backup_codes?.length) {
        setPendingTokens({ access_token: tokens.access_token, expires_in: tokens.expires_in });
        setBackupCodes(tokens.backup_codes);
        return;
      }
      await onComplete(tokens);
    } catch (err: any) {
      const detail = err.response?.data?.detail ?? err.response?.data?.message;
      onError(typeof detail === 'string' ? detail : 'Invalid verification code');
    } finally {
      setSubmitting(false);
    }
  };

  if (backupCodes) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Save your backup codes</h3>
        <p className="text-sm text-gray-600">
          Store these one-time recovery codes somewhere safe. Each can be used once if you lose access to your authenticator.
        </p>
        <div className="p-4 bg-gray-50 rounded-md border border-gray-200 grid grid-cols-2 gap-2">
          {backupCodes.map((c) => (
            <code key={c} className="text-sm font-mono tracking-wide text-gray-900">
              {c}
            </code>
          ))}
        </div>
        <button
          type="button"
          onClick={() => pendingTokens && onComplete(pendingTokens)}
          disabled={!pendingTokens}
          className="flex justify-center px-4 py-2 w-full text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          I&apos;ve saved my codes — continue
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          {setupRequired ? 'Set up two-factor authentication' : 'Two-factor authentication'}
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          {setupRequired
            ? 'Your organization requires an authenticator app. Scan the setup details below, then enter a code to finish signing in.'
            : 'Verify your identity to continue.'}
        </p>
      </div>

      {setupRequired && (
        <div className="p-4 bg-gray-50 rounded-md border border-gray-200 space-y-3">
          {loadingSetup ? (
            <p className="text-sm text-gray-600">Preparing setup…</p>
          ) : (
            <>
              {provisioningUri && (
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-1">Setup URI</p>
                  <p className="text-xs break-all text-gray-600 font-mono">{provisioningUri}</p>
                  <p className="mt-2 text-xs text-gray-500">
                    Add this account in Google Authenticator, 1Password, Authy, or another TOTP app.
                  </p>
                </div>
              )}
              {secret && (
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-1">Manual entry key</p>
                  <p className="text-sm font-mono tracking-wider text-gray-900">{secret}</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!setupRequired && (
        <div className="flex rounded-md border border-gray-200 overflow-hidden text-sm">
          {(['totp', 'email', 'backup'] as MfaMethod[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => {
                setMethod(m);
                setCode('');
              }}
              className={`flex-1 px-3 py-2 font-medium ${
                method === m ? 'bg-blue-50 text-blue-700' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {m === 'totp' ? 'Authenticator' : m === 'email' ? 'Email' : 'Backup code'}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {method === 'email' && !setupRequired && (
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-gray-600">
              {emailSent ? 'Code sent — check your inbox.' : 'Send a one-time code to your account email.'}
            </p>
            <button
              type="button"
              onClick={handleSendEmailCode}
              disabled={sendingEmail}
              className="shrink-0 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 disabled:opacity-50"
            >
              {sendingEmail ? 'Sending…' : emailSent ? 'Resend' : 'Send code'}
            </button>
          </div>
        )}

        <div>
          <label htmlFor="mfa-code" className="block text-sm font-medium text-gray-700">
            {method === 'backup'
              ? 'Backup recovery code'
              : method === 'email'
                ? 'Email verification code'
                : 'Authenticator code'}
          </label>
          <input
            id="mfa-code"
            inputMode={method === 'backup' ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            maxLength={method === 'backup' ? 9 : 6}
            required
            value={code}
            onChange={(e) => {
              const raw = e.target.value;
              if (method === 'backup') {
                setCode(raw.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 9));
              } else {
                setCode(raw.replace(/\D/g, '').slice(0, 6));
              }
            }}
            className={`block px-3 py-2 mt-1 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
              method === 'backup' ? 'font-mono tracking-wider text-center' : 'tracking-widest text-center text-lg'
            }`}
            placeholder={method === 'backup' ? 'XXXX-XXXX' : '000000'}
          />
        </div>
        <button
          type="submit"
          disabled={submitting || (setupRequired && loadingSetup) || (method === 'email' && !emailSent && !setupRequired)}
          className="flex justify-center px-4 py-2 w-full text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Verifying…' : setupRequired ? 'Enable MFA and continue' : 'Verify and sign in'}
        </button>
      </form>
    </div>
  );
};

export default MfaChallengePanel;
